import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './VigilanciaAtivaTab.css';


// eslint-disable-next-line no-unused-vars
const IndicadorCard = ({ titulo, valor }) => (
    <div className="indicador-card">
        <div className="indicador-titulo">{titulo}</div>
        <div className="indicador-valor">{valor}</div>
    </div>
);

// eslint-disable-next-line no-unused-vars
const ChevronDownIcon = () => (
    <svg height="20" width="20" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
);

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" height="20" width="20">
        <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
    </svg>
);


const ExameStatusBadge = ({ exame }) => {
    if (!exame) {
        return null;
    }
    const exemplares = exame.exames_exemplares;
    if (!exemplares || exemplares.length === 0) {
        return <span className="exame-status-badge aguardando">Aguardando Laudo</span>;
    }
    const hasPositive = exemplares.some(ex => ex.resultado?.toLowerCase().includes('positivo'));
    if (hasPositive) {
        return <span className="exame-status-badge positivo">Positivo</span>;
    }
    const hasPending = exemplares.some(ex => !ex.resultado || ex.resultado.toLowerCase() === 'não examinado');
    if (hasPending) {
        return <span className="exame-status-badge aguardando">Em Análise</span>;
    }
    return <span className="exame-status-badge negativo">Negativo</span>;
};


// eslint-disable-next-line no-unused-vars
const getExameStatus = (exame) => {
    if (!exame) return 'pendente';
    const exemplares = exame.exames_exemplares;
    if (!exemplares || exemplares.length === 0) return 'pendente';
    if (exemplares.some(ex => ex.resultado?.toLowerCase().includes('positivo'))) return 'positivo';
    if (exemplares.some(ex => !ex.resultado || ex.resultado.toLowerCase() === 'não examinado')) return 'pendente';
    return 'negativo';
};



/* ─── Card (lista) ─── */
const CasoCard = ({ caso, onCardClick, onImageClick }) => {
    const todasAsImagens = useMemo(() => {
        const imgs = [];
        if (caso.foto_principal) imgs.push(caso.foto_principal);
        if (caso.fotos_urls?.length > 0) caso.fotos_urls.forEach(u => { if (!imgs.includes(u)) imgs.push(u); });
        return imgs;
    }, [caso.foto_principal, caso.fotos_urls]);

    const statusStyle = (() => {
        switch (caso.origem_status) {
            case 'Denúncia Pendente': return { bg: '#fffbeb', color: '#92400e', border: '#fde68a' };
            case 'Denúncia Atendida': return { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' };
            case 'Denúncia Recusada': return { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' };
            default:                  return { bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe' };
        }
    })();

    const formatDT = (ds) => ds ? new Date(ds).toLocaleString('pt-BR', { timeZone: 'UTC' }) : '—';

    return (
        <div
            className={`caso-card denuncia-list-item${caso.denuncia_status === 'recusada' ? ' recusada' : ''}`}
            onClick={() => onCardClick(caso)}
            style={{ cursor: 'pointer' }}
        >
            <div className="denuncia-list-row">

                {/* Foto grande à esquerda */}
                <div
                    className="denuncia-foto-wrap"
                    onClick={(e) => { e.stopPropagation(); if (todasAsImagens.length > 0) onImageClick(todasAsImagens, 0); }}
                >
                    {caso.foto_principal ? (
                        <>
                            <img src={caso.foto_principal} alt="" className="denuncia-foto-img" />
                            <div className="denuncia-foto-overlay">
                                <span className="denuncia-foto-ampliar">
                                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} />
                                    Ampliar
                                </span>
                            </div>
                        </>
                    ) : (
                        <div className="denuncia-foto-empty">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                            </svg>
                        </div>
                    )}
                </div>

                {/* Informações */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Status + badge exame + data */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <span style={{ padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap', background: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}` }}>
                            {caso.origem_status?.replace('Denúncia ', '') || '—'}
                        </span>
                        <ExameStatusBadge exame={caso.exame} />
                        <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                            {formatDT(caso.denuncia_data)}
                        </span>
                    </div>

                    {/* Campos */}
                    <div className="denuncia-card-fields">
                        <div className="dcf-row"><span className="dcf-label">Endereço</span><span className="dcf-value">{[caso.denuncia_rua, caso.denuncia_numero].filter(Boolean).join(', ') || '—'}</span></div>
                        <div className="dcf-row"><span className="dcf-label">Bairro</span><span className="dcf-value">{caso.denuncia_bairro || 'Não informado'}</span></div>
                        {caso.denuncia_complemento && <div className="dcf-row"><span className="dcf-label">Complemento</span><span className="dcf-value">{caso.denuncia_complemento}</span></div>}
                        {caso.denuncia_descricao && <div className="dcf-row"><span className="dcf-label">Descrição</span><span className="dcf-value">{caso.denuncia_descricao}</span></div>}
                        <div className="dcf-row"><span className="dcf-label">Status</span><span className="dcf-value">{caso.denuncia_status || '—'}</span></div>
                        {caso.denuncia_feedback && (
                            <div className="dcf-row dcf-feedback">
                                <span className="dcf-label">Feedback</span>
                                <span className="dcf-value dcf-feedback-text">"{caso.denuncia_feedback}"</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};


/* ─── Helpers do modal de detalhes ─── */
const NI = ({ children }) => children
    ? <span>{children}</span>
    : <em style={{ color: '#94a3b8' }}>Não informado</em>;

const DetailRow = ({ label, value }) => (
    <li><strong>{label}:</strong> <NI>{value}</NI></li>
);

const toThumb = (url, width = 400) => {
    if (!url || !url.includes('/storage/v1/object/public/')) return url;
    return url.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/') + `?width=${width}&quality=65`;
};

const parseFotosField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field.filter(Boolean);
    if (typeof field === 'string') {
        const t = field.trim();
        if (t.startsWith('{') && t.endsWith('}')) {
            return t.slice(1, -1).split(',').map(s => s.trim().replace(/^"|"$/g, '')).filter(Boolean);
        }
        try { const p = JSON.parse(t); if (Array.isArray(p)) return p.filter(Boolean); } catch {}
    }
    return [];
};

/* ─── Modal de detalhes da denúncia ─── */
const DenunciaDetailModal = ({ caso, onClose, onAtender, onRecusar, onImageClick }) => {
    const [isRecusando, setIsRecusando] = useState(false);
    const [motivoRecusa, setMotivoRecusa] = useState('');

    const fmtDate = (ds) => ds ? new Date(`${ds}T00:00:00.000Z`).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : null;
    const fmtDT   = (ds) => ds ? new Date(ds).toLocaleString('pt-BR', { timeZone: 'UTC' }) : null;

    const formatActivities = (a) => {
        if (!a) return null;
        let list = Array.isArray(a) ? a : (() => {
            const t = String(a).trim();
            if (t.startsWith('{') && t.endsWith('}')) return t.slice(1,-1).split(',').map(s=>s.trim().replace(/^"|"$/g,''));
            try { const p = JSON.parse(t); return Array.isArray(p) ? p : [t]; } catch(e) { return t.split(',').map(s=>s.trim()); }
        })();
        const result = list.map(i => {
            const v = String(i).toLowerCase();
            if (v.includes('pesquisa')) return 'Pesquisa';
            if (v.includes('borrifac')) return 'Borrifação';
            if (v.includes('pit')) return 'Atendimento ao PIT';
            return i.charAt(0).toUpperCase()+i.slice(1);
        }).join(', ');
        return result || null;
    };

    const formatPendencia = (text) => {
        if (!text) return null;
        const l = text.toLowerCase();
        if (l === 'sempendencias') return 'Sem Pendências';
        if (l === 'fechado' || l === 'domicilio fechado') return 'Domicílio Fechado';
        if (l === 'recusa' || l === 'recusado') return 'Recusa';
        return text;
    };

    const formatMelhoria = (v) => {
        if (v === true) return 'Sim';
        if (v === false) return 'Não';
        return null;
    };

    const MATERIAL_MAP = {
        alvenaria_reboco: 'Alvenaria c/ reboco', alvenaria_sem_reboco: 'Alvenaria s/ reboco',
        barro_reboco: 'Barro c/ reboco', barro_sem_reboco: 'Barro s/ reboco',
        madeira: 'Madeira', taipa: 'Taipa', palha: 'Palha', telha: 'Telha',
        metalico: 'Metálico', outros: 'Outros',
        nova: 'Nova (primeira pesquisa)', reconhecida: 'Reconhecida', demolida: 'Demolida',
    };
    const formatMaterial = (v) => (v && MATERIAL_MAP[v]) ? MATERIAL_MAP[v] : (v || null);

    const displayId = caso.denuncia_id ? `D-${caso.denuncia_id}` : `A-${caso.id}`;

    const fotoDenuncia = caso.foto_principal || null;
    const fotosAtendimento = parseFotosField(caso.fotos_urls).filter(u => u !== fotoDenuncia);
    const todasAsImagens = [fotoDenuncia, ...fotosAtendimento].filter(Boolean);

    return (
        <div className="modal-overlay" onClick={onClose} style={{ zIndex: 2000 }}>
            <div className="denuncia-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="denuncia-modal-header">
                    <div>
                        <h2 className="denuncia-modal-title">Denúncia #{displayId}</h2>
                        <span className="denuncia-modal-date">{fmtDT(caso.denuncia_data)}</span>
                    </div>
                    <button className="modal-close-btn" onClick={onClose}>×</button>
                </div>

                <div className="denuncia-modal-body">

                    {/* ── 1. DENÚNCIA ── */}
                    <div className="denuncia-modal-section">
                        <h3 className="section-title">Denúncia</h3>

                        {fotoDenuncia && (
                            <div className="denuncia-foto-wrapper" style={{ marginBottom: '16px' }}>
                                <img
                                    src={toThumb(fotoDenuncia, 600)}
                                    alt="Foto da denúncia"
                                    style={{ width: '100%', maxHeight: '260px', objectFit: 'cover', borderRadius: '8px', display: 'block', cursor: 'zoom-in' }}
                                    loading="lazy"
                                    onClick={() => onImageClick(todasAsImagens, 0)}
                                />
                            </div>
                        )}

                        <ul className="details-list">
                            {caso.denuncia_id && (
                                <li style={{ fontFamily: 'monospace', fontSize: '11px', color: '#94a3b8' }}>ID: {caso.denuncia_id}</li>
                            )}
                            <DetailRow label="Data / Hora" value={fmtDT(caso.denuncia_data)} />
                            <DetailRow label="Status" value={caso.denuncia_status} />
                            <DetailRow label="Endereço" value={[caso.denuncia_rua, caso.denuncia_numero].filter(Boolean).join(', ') || null} />
                            <DetailRow label="Bairro" value={caso.denuncia_bairro} />
                            <DetailRow label="Complemento" value={caso.denuncia_complemento} />
                            <DetailRow label="Descrição" value={caso.denuncia_descricao} />
                            {caso.denuncia_feedback && (
                                <li style={{ marginTop: 8, padding: '10px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd', listStyle: 'none' }}>
                                    <strong style={{ color: '#0369a1' }}>Feedback ao Cidadão:</strong>
                                    <p style={{ margin: '4px 0 0', fontStyle: 'italic', color: '#0369a1' }}>"{caso.denuncia_feedback}"</p>
                                </li>
                            )}
                        </ul>
                    </div>

                    {/* ── 2. VISITA TÉCNICA DO AGENTE ── */}
                    {caso.id && (
                        <div className="denuncia-modal-section">
                            <h3 className="section-title">🔬 Visita Técnica do Agente</h3>

                            <h5 className="subsection-title">Identificação</h5>
                            <ul className="details-list">
                                <DetailRow label="Agente" value={caso.agente_nome} />
                                <DetailRow label="Data da Visita" value={fmtDate(caso.data_atividade)} />
                                <DetailRow label="Tipo de Atividade" value={formatActivities(caso.tipo_atividade)} />
                                <DetailRow label="Nº do PIT" value={caso.numero_pit} />
                                <DetailRow label="Código de Etiqueta" value={caso.codigo_etiqueta} />
                            </ul>

                            <h5 className="subsection-title">Domicílio</h5>
                            <ul className="details-list">
                                <DetailRow label="Localidade" value={caso.localidade} />
                                <DetailRow label="Cód. Localidade" value={caso.codigo_localidade} />
                                <DetailRow label="Categoria" value={caso.categoria_localidade} />
                                <DetailRow label="Endereço" value={caso.endereco} />
                                <DetailRow label="Número" value={caso.numero} />
                                <DetailRow label="Complemento" value={caso.complemento} />
                                <DetailRow label="Nome do Morador" value={caso.nome_morador} />
                                <DetailRow label="Qtd. Moradores" value={caso.quantidade_moradores != null ? String(caso.quantidade_moradores) : null} />
                                <DetailRow label="Nº de Anexos" value={caso.numero_anexo != null ? String(caso.numero_anexo) : null} />
                            </ul>

                            <h5 className="subsection-title">Estrutura do Imóvel</h5>
                            <ul className="details-list-grid">
                                <DetailRow label="Situação" value={formatMaterial(caso.situacao_imovel)} />
                                <DetailRow label="Tipo de Parede" value={formatMaterial(caso.tipo_parede)} />
                                <DetailRow label="Tipo de Teto" value={formatMaterial(caso.tipo_teto)} />
                                <DetailRow label="Melhoria Habitacional" value={formatMelhoria(caso.melhoria_habitacional)} />
                                <DetailRow label="Pendência Pesquisa" value={formatPendencia(caso.pendencia_pesquisa)} />
                                <DetailRow label="Pendência Borrifação" value={formatPendencia(caso.pendencia_borrifacao)} />
                            </ul>

                            <h5 className="subsection-title">Captura de Triatomíneos</h5>
                            <ul className="details-list-grid">
                                <DetailRow label="Vestígios (Intra)" value={caso.vestigios_intradomicilio} />
                                <DetailRow label="Barbeiros (Intra)" value={caso.barbeiros_intradomicilio != null ? String(caso.barbeiros_intradomicilio) : null} />
                                <DetailRow label="Vestígios (Peri)" value={caso.vestigios_peridomicilio} />
                                <DetailRow label="Barbeiros (Peri)" value={caso.barbeiros_peridomicilio != null ? String(caso.barbeiros_peridomicilio) : null} />
                            </ul>

                            <h5 className="subsection-title">Borrifação</h5>
                            <ul className="details-list-grid">
                                <DetailRow label="Inseticida" value={caso.inseticida} />
                                <DetailRow label="Nº de Cargas" value={caso.numero_cargas != null ? String(caso.numero_cargas) : null} />
                            </ul>

                            <h5 className="subsection-title">Registros do Atendimento</h5>
                            {fotosAtendimento.length > 0 ? (
                                <div className="gallery-container">
                                    {fotosAtendimento.map((url, i) => (
                                        <div key={i} className="gallery-thumbnail-wrapper" onClick={() => onImageClick(todasAsImagens, todasAsImagens.indexOf(url))}>
                                            <img src={toThumb(url, 300)} alt={`Foto ${i + 1}`} className="gallery-thumbnail" loading="lazy" />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="no-data-text">Nenhuma foto registrada pelo agente.</p>
                            )}
                        </div>
                    )}

                    {/* ── 3. ANÁLISE LABORATORIAL ── */}
                    {caso.exame && (
                        <div className="denuncia-modal-section">
                            <h3 className="section-title">🧫 Análise Laboratorial</h3>
                            <ul className="details-list">
                                <DetailRow label="Técnico Responsável" value={caso.exame.tecnico_responsavel} />
                                <DetailRow label="Data do Formulário de Campo" value={fmtDate(caso.exame.data_exame)} />
                                <DetailRow label="Situação" value={caso.exame.situacao} />
                                <DetailRow label="Cód. Atividade Lab." value={caso.exame.codigo_atividade_lab} />
                            </ul>
                            <h5 className="subsection-title">Exemplares Examinados ({caso.exame.exames_exemplares?.length || 0})</h5>
                            <div className="exemplares-list">
                                {(caso.exame.exames_exemplares || []).length > 0
                                    ? (caso.exame.exames_exemplares || []).map((ex, idx) => {
                                        const isPositivo = ex.resultado?.toLowerCase().includes('positivo');
                                        return (
                                            <div key={ex.id || idx} className="exemplar-item">
                                                <div className="exemplar-grid">
                                                    <div><strong>Vetor:</strong> {ex.vetor || 'N/A'}</div>
                                                    <div><strong>Estágio:</strong> {ex.estagio || 'N/A'}</div>
                                                    <div><strong>Captura:</strong> {ex.captura || 'N/A'}</div>
                                                </div>
                                                <div className="exemplar-resultado">
                                                    <strong>Resultado:</strong>
                                                    <span style={{ fontWeight: 'bold', color: isPositivo ? '#B91C1C' : '#374151' }}>
                                                        {ex.resultado || 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })
                                    : <p className="no-data-text">Nenhum exemplar examinado.</p>
                                }
                            </div>
                        </div>
                    )}

                    {/* ── Ações ── */}
                    {caso.origem_status === 'Denúncia Pendente' && (
                        <div className="card-actions-section" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '8px' }}>
                            {!isRecusando ? (
                                <>
                                    <button className="atender-button atender-button-primary" onClick={() => onAtender(caso)}>
                                        Atender Denúncia
                                    </button>
                                    <button className="atender-button atender-button-outline" onClick={() => setIsRecusando(true)}>
                                        Recusar
                                    </button>
                                </>
                            ) : (
                                <div className="recusa-form-container">
                                    <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#1e293b' }}>Mensagem para o Cidadão</h4>
                                    <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#64748b' }}>Esta mensagem aparecerá no app do morador.</p>
                                    <textarea placeholder="Ex: Endereço não localizado; Denúncia improcedente..." value={motivoRecusa} onChange={e => setMotivoRecusa(e.target.value)} />
                                    <div className="recusa-actions">
                                        <button className="btn-confirm-recusa" disabled={!motivoRecusa.trim()} onClick={() => { onRecusar(caso.denuncia_id, motivoRecusa); onClose(); }}>
                                            Confirmar Recusa
                                        </button>
                                        <button className="btn-cancel-recusa" onClick={() => { setIsRecusando(false); setMotivoRecusa(''); }}>
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────
   LINHA_VAZIA — reservada para uso futuro
───────────────────────────────────────────── */
const LINHA_VAZIA = {
    data: '', localidade: '', concluida: '',
    ct_pos: '', ct_neg: '', ct_total: '',
    cp_fech: '', cp_rec: '', cp_total: '',
    at_pos: '', at_neg: '', at_total: '',
    ud_pos: '', ud_neg: '', ud_total: '',
    hab_proteg: '', consumo_inset: '', n_triat: '', hd: ''
};

const BorrifacaoTab = ({ municipioId }) => { // eslint-disable-line no-unused-vars
    const [cabecalho, setCabecalho] = useState({
        dist_sanit: '', sub_distrito: '', turma: '',
        guarda_chefe: '', municipio: '', fase_trabalho: '', semana: ''
    });
    const [linhas, setLinhas] = useState(() => Array.from({ length: 10 }, () => ({ ...LINHA_VAZIA, _id: Math.random() })));
    const [rodape, setRodape] = useState({ rendimento: '', saldo_bhc: '', ass_guarda: '', ass_inspetor: '', data_ass: '' });

    const [salvando, setSalvando] = useState(false);
    const [resumos, setResumos] = useState([]);
    const [loadingResumos, setLoadingResumos] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [editandoId, setEditandoId] = useState(null); // id do resumo sendo editado
    const formRef = useRef(null);

    const setCab = (f, v) => setCabecalho(p => ({ ...p, [f]: v }));
    const setRod = (f, v) => setRodape(p => ({ ...p, [f]: v }));

    const fetchResumos = useCallback(async () => {
        if (!municipioId) return;
        setLoadingResumos(true);
        try {
            const { data, error } = await supabase
                .from('resumos_borrifacao')
                .select('*, resumos_borrifacao_linhas(*)')
                .eq('municipio_id', municipioId)
                .order('created_at', { ascending: false });
            if (error) throw error;
            setResumos(data || []);
        } catch (err) {
            console.error('Erro ao buscar resumos:', err);
        } finally {
            setLoadingResumos(false);
        }
    }, [municipioId]);

    useEffect(() => { fetchResumos(); }, [fetchResumos]);

    const handleCarregarParaEdicao = (r) => {
        setEditandoId(r.id);
        setCabecalho({
            dist_sanit:   r.dist_sanit   || '',
            sub_distrito: r.sub_distrito || '',
            turma:        r.turma        || '',
            guarda_chefe: r.guarda_chefe || '',
            municipio:    r.municipio_nome || '',
            fase_trabalho:r.fase_trabalho|| '',
            semana:       r.semana       || '',
        });
        const linhasCarregadas = (r.resumos_borrifacao_linhas || [])
            .sort((a, b) => a.ordem - b.ordem)
            .map(l => ({
                _id: Math.random(),
                data:          l.data          || '',
                localidade:    l.localidade    || '',
                concluida:     l.concluida     || '',
                ct_pos:        l.ct_pos  != null ? String(l.ct_pos)  : '',
                ct_neg:        l.ct_neg  != null ? String(l.ct_neg)  : '',
                ct_total:      l.ct_total!= null ? String(l.ct_total): '',
                cp_fech:       l.cp_fech != null ? String(l.cp_fech) : '',
                cp_rec:        l.cp_rec  != null ? String(l.cp_rec)  : '',
                cp_total:      l.cp_total!= null ? String(l.cp_total): '',
                at_pos:        l.at_pos  != null ? String(l.at_pos)  : '',
                at_neg:        l.at_neg  != null ? String(l.at_neg)  : '',
                at_total:      l.at_total!= null ? String(l.at_total): '',
                ud_pos:        l.ud_pos  != null ? String(l.ud_pos)  : '',
                ud_neg:        l.ud_neg  != null ? String(l.ud_neg)  : '',
                ud_total:      l.ud_total!= null ? String(l.ud_total): '',
                hab_proteg:    l.hab_proteg    != null ? String(l.hab_proteg)    : '',
                consumo_inset: l.consumo_inset != null ? String(l.consumo_inset) : '',
                n_triat:       l.n_triat       != null ? String(l.n_triat)       : '',
                hd:            l.hd            || '',
            }));
        setLinhas(linhasCarregadas.length > 0 ? linhasCarregadas : Array.from({ length: 10 }, () => ({ ...LINHA_VAZIA, _id: Math.random() })));
        setRodape({
            rendimento:   r.rendimento   || '',
            saldo_bhc:    r.saldo_bhc    || '',
            ass_guarda:   r.ass_guarda   || '',
            ass_inspetor: r.ass_inspetor || '',
            data_ass:     r.data_ass     || '',
        });
        setExpandedId(null);
        setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    };

    const handleExcluir = async (id) => {
        if (!window.confirm('Excluir este resumo semanal? Esta ação não pode ser desfeita.')) return;
        try {
            const { error } = await supabase.from('resumos_borrifacao').delete().eq('id', id);
            if (error) throw error;
            if (editandoId === id) {
                setEditandoId(null);
                setCabecalho({ dist_sanit: '', sub_distrito: '', turma: '', guarda_chefe: '', municipio: '', fase_trabalho: '', semana: '' });
                setLinhas(Array.from({ length: 10 }, () => ({ ...LINHA_VAZIA, _id: Math.random() })));
                setRodape({ rendimento: '', saldo_bhc: '', ass_guarda: '', ass_inspetor: '', data_ass: '' });
            }
            fetchResumos();
        } catch (err) {
            console.error(err);
            alert('Erro ao excluir resumo.');
        }
    };

    const handleSalvar = async () => {
        if (!municipioId) { alert('Município não identificado.'); return; }
        if (!cabecalho.semana) { alert('Informe a semana antes de salvar.'); return; }
        setSalvando(true);
        try {
            const payload = {
                municipio_id:   municipioId,
                dist_sanit:     cabecalho.dist_sanit     || null,
                sub_distrito:   cabecalho.sub_distrito   || null,
                turma:          cabecalho.turma           || null,
                guarda_chefe:   cabecalho.guarda_chefe   || null,
                municipio_nome: cabecalho.municipio      || null,
                fase_trabalho:  cabecalho.fase_trabalho  || null,
                semana:         cabecalho.semana         || null,
                rendimento:     rodape.rendimento        || null,
                saldo_bhc:      rodape.saldo_bhc         || null,
                ass_guarda:     rodape.ass_guarda        || null,
                ass_inspetor:   rodape.ass_inspetor      || null,
                data_ass:       rodape.data_ass          || null,
            };

            let resumoId;
            if (editandoId) {
                const { error: errUp } = await supabase.from('resumos_borrifacao').update(payload).eq('id', editandoId);
                if (errUp) throw errUp;
                await supabase.from('resumos_borrifacao_linhas').delete().eq('resumo_id', editandoId);
                resumoId = editandoId;
            } else {
                const { data: resumo, error: errResumo } = await supabase
                    .from('resumos_borrifacao').insert(payload).select().single();
                if (errResumo) throw errResumo;
                resumoId = resumo.id;
            }

            const linhasValidas = linhas.filter(l => l.localidade || l.data);
            if (linhasValidas.length > 0) {
                const linhasPayload = linhasValidas.map((l, idx) => ({
                    resumo_id: resumoId,
                    ordem: idx,
                    data: l.data || null,
                    localidade: l.localidade || null,
                    concluida: l.concluida || null,
                    ct_pos: Number(l.ct_pos) || null,
                    ct_neg: Number(l.ct_neg) || null,
                    ct_total: Number(l.ct_total) || null,
                    cp_fech: Number(l.cp_fech) || null,
                    cp_rec: Number(l.cp_rec) || null,
                    cp_total: Number(l.cp_total) || null,
                    at_pos: Number(l.at_pos) || null,
                    at_neg: Number(l.at_neg) || null,
                    at_total: Number(l.at_total) || null,
                    ud_pos: Number(l.ud_pos) || null,
                    ud_neg: Number(l.ud_neg) || null,
                    ud_total: Number(l.ud_total) || null,
                    hab_proteg: Number(l.hab_proteg) || null,
                    consumo_inset: Number(l.consumo_inset) || null,
                    n_triat: Number(l.n_triat) || null,
                    hd: l.hd || null,
                }));
                const { error: errLinhas } = await supabase.from('resumos_borrifacao_linhas').insert(linhasPayload);
                if (errLinhas) throw errLinhas;
            }

            alert(editandoId ? 'Resumo atualizado com sucesso!' : 'Resumo salvo com sucesso!');
            setEditandoId(null);
            setCabecalho({ dist_sanit: '', sub_distrito: '', turma: '', guarda_chefe: '', municipio: '', fase_trabalho: '', semana: '' });
            setLinhas(Array.from({ length: 10 }, () => ({ ...LINHA_VAZIA, _id: Math.random() })));
            setRodape({ rendimento: '', saldo_bhc: '', ass_guarda: '', ass_inspetor: '', data_ass: '' });
            fetchResumos();
        } catch (err) {
            console.error(err);
            alert('Erro ao salvar resumo.');
        } finally {
            setSalvando(false);
        }
    };

    const setLinha = (idx, field, value) => {
        setLinhas(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
    };

    const addLinha = () => setLinhas(prev => [...prev, { ...LINHA_VAZIA, _id: Math.random() }]);
    const removeLinha = (idx) => setLinhas(prev => prev.filter((_, i) => i !== idx));

    /* auto-totais */
    const withTotals = linhas.map(l => ({
        ...l,
        ct_total: (Number(l.ct_pos) || 0) + (Number(l.ct_neg) || 0) || '',
        cp_total: (Number(l.cp_fech) || 0) + (Number(l.cp_rec) || 0) || '',
        at_total: (Number(l.at_pos) || 0) + (Number(l.at_neg) || 0) || '',
        ud_total: (Number(l.ud_pos) || 0) + (Number(l.ud_neg) || 0) || '',
    }));

    const totalRow = withTotals.reduce((acc, l) => ({
        ct_pos:  (acc.ct_pos  || 0) + (Number(l.ct_pos)  || 0),
        ct_neg:  (acc.ct_neg  || 0) + (Number(l.ct_neg)  || 0),
        ct_total:(acc.ct_total|| 0) + (Number(l.ct_total) || 0),
        cp_fech: (acc.cp_fech || 0) + (Number(l.cp_fech) || 0),
        cp_rec:  (acc.cp_rec  || 0) + (Number(l.cp_rec)  || 0),
        cp_total:(acc.cp_total|| 0) + (Number(l.cp_total) || 0),
        at_pos:  (acc.at_pos  || 0) + (Number(l.at_pos)  || 0),
        at_neg:  (acc.at_neg  || 0) + (Number(l.at_neg)  || 0),
        at_total:(acc.at_total|| 0) + (Number(l.at_total) || 0),
        ud_pos:  (acc.ud_pos  || 0) + (Number(l.ud_pos)  || 0),
        ud_neg:  (acc.ud_neg  || 0) + (Number(l.ud_neg)  || 0),
        ud_total:(acc.ud_total|| 0) + (Number(l.ud_total) || 0),
        hab_proteg:    (acc.hab_proteg    || 0) + (Number(l.hab_proteg)    || 0),
        consumo_inset: (acc.consumo_inset || 0) + (Number(l.consumo_inset) || 0),
        n_triat:       (acc.n_triat       || 0) + (Number(l.n_triat)       || 0),
    }), {});

    const sIn = { border: '1px solid #aaa', padding: '2px 4px', fontSize: '11px', width: '100%', boxSizing: 'border-box', borderRadius: '2px' };
    const sNum = { ...sIn, textAlign: 'center', width: '40px' };
    const sTh = { border: '1px solid #555', background: '#d0d0d0', fontSize: '10px', fontWeight: '700', textAlign: 'center', padding: '3px 2px', verticalAlign: 'middle' };
    const sTd = { border: '1px solid #bbb', padding: '2px', verticalAlign: 'middle' };
    const sTot = { ...sTd, background: '#f0f0f0', fontWeight: '700', textAlign: 'center', fontSize: '11px' };

    /* KPIs calculados de todos os resumos salvos */
    const kpis = useMemo(() => {
        const todasLinhas = resumos.flatMap(r => r.resumos_borrifacao_linhas || []);
        const soma = (campo) => todasLinhas.reduce((s, l) => s + (Number(l[campo]) || 0), 0);
        return {
            casas_trabalhadas: soma('ct_total'),
            casas_positivas:   soma('ct_pos'),
            casas_negativas:   soma('ct_neg'),
            hab_protegidos:    soma('hab_proteg'),
            inseticida:        soma('consumo_inset'),
            triatominos:       soma('n_triat'),
            total_resumos:     resumos.length,
        };
    }, [resumos]);

    return (
        <div style={{ padding: '16px 0' }} ref={formRef}>

            {/* ── Dashboard de KPIs ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '32px' }}>
                {[
                    { label: 'Resumos Cadastrados',     valor: kpis.total_resumos,     cor: '#0d9488', bg: '#f0fdfa' },
                    { label: 'Casas Trabalhadas',        valor: kpis.casas_trabalhadas, cor: '#2563eb', bg: '#eff6ff' },
                    { label: 'Casas Positivas',          valor: kpis.casas_positivas,   cor: '#dc2626', bg: '#fef2f2' },
                    { label: 'Casas Negativas',          valor: kpis.casas_negativas,   cor: '#16a34a', bg: '#f0fdf4' },
                    { label: 'Habitantes Protegidos',    valor: kpis.hab_protegidos,    cor: '#7c3aed', bg: '#f5f3ff' },
                    { label: 'Inseticida (cps)',          valor: kpis.inseticida,        cor: '#d97706', bg: '#fffbeb' },
                    { label: 'Triatomíneos Capturados',  valor: kpis.triatominos,       cor: '#0891b2', bg: '#ecfeff' },
                ].map(({ label, valor, cor, bg }) => (
                    <div key={label} style={{ background: bg, border: `1px solid ${cor}22`, borderRadius: '10px', padding: '14px 16px' }}>
                        <div style={{ fontSize: '10px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{label}</div>
                        <div style={{ fontSize: '26px', fontWeight: '800', color: cor, lineHeight: 1 }}>{valor.toLocaleString('pt-BR')}</div>
                    </div>
                ))}
            </div>

            {/* Divisor com título do formulário */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', whiteSpace: 'nowrap' }}>Novo Resumo Semanal</span>
                <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
            </div>

            {/* Título + indicador de edição */}
            <div style={{ textAlign: 'center', fontWeight: '800', fontSize: '13px', letterSpacing: '0.5px', marginBottom: '4px' }}>
                RESUMO SEMANAL DE BORRIFAÇÃO
            </div>
            {editandoId && (
                <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                    <span style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '20px', padding: '2px 14px', fontSize: '11px', fontWeight: '700', color: '#92400e' }}>
                        ✏️ Modo Edição — alterando resumo existente
                    </span>
                    <button onClick={() => { setEditandoId(null); setCabecalho({ dist_sanit:'',sub_distrito:'',turma:'',guarda_chefe:'',municipio:'',fase_trabalho:'',semana:'' }); setLinhas(Array.from({length:10},()=>({...LINHA_VAZIA,_id:Math.random()}))); setRodape({rendimento:'',saldo_bhc:'',ass_guarda:'',ass_inspetor:'',data_ass:''}); }} style={{ marginLeft: '10px', fontSize: '11px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', textDecoration: 'underline' }}>Cancelar edição</button>
                </div>
            )}

            {/* Cabeçalho — Linha 1 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr 1fr', gap: '6px', marginBottom: '6px', fontSize: '12px' }}>
                <div>
                    <label style={{ fontSize: '10px', color: '#555', display: 'block', marginBottom: '2px' }}>DIST. SANIT.</label>
                    <input style={sIn} value={cabecalho.dist_sanit} onChange={e => setCab('dist_sanit', e.target.value)} />
                </div>
                <div>
                    <label style={{ fontSize: '10px', color: '#555', display: 'block', marginBottom: '2px' }}>SUB-DISTRITO</label>
                    <input style={sIn} value={cabecalho.sub_distrito} onChange={e => setCab('sub_distrito', e.target.value)} />
                </div>
                <div>
                    <label style={{ fontSize: '10px', color: '#555', display: 'block', marginBottom: '2px' }}>MUNICÍPIO</label>
                    <input style={sIn} value={cabecalho.municipio} onChange={e => setCab('municipio', e.target.value)} />
                </div>
                <div>
                    <label style={{ fontSize: '10px', color: '#555', display: 'block', marginBottom: '2px' }}>FASE DE TRABALHO</label>
                    <input style={sIn} value={cabecalho.fase_trabalho} onChange={e => setCab('fase_trabalho', e.target.value)} />
                </div>
            </div>

            {/* Cabeçalho — Linha 2 */}
            <div style={{ display: 'grid', gridTemplateColumns: '80px 80px 1fr', gap: '6px', marginBottom: '14px', fontSize: '12px' }}>
                <div>
                    <label style={{ fontSize: '10px', color: '#555', display: 'block', marginBottom: '2px' }}>SEMANA</label>
                    <input style={sIn} value={cabecalho.semana} onChange={e => setCab('semana', e.target.value)} />
                </div>
                <div>
                    <label style={{ fontSize: '10px', color: '#555', display: 'block', marginBottom: '2px' }}>TURMA</label>
                    <input style={sIn} value={cabecalho.turma} onChange={e => setCab('turma', e.target.value)} />
                </div>
                <div>
                    <label style={{ fontSize: '10px', color: '#555', display: 'block', marginBottom: '2px' }}>NOME GUARDA CHEFE</label>
                    <input style={sIn} value={cabecalho.guarda_chefe} onChange={e => setCab('guarda_chefe', e.target.value)} />
                </div>
            </div>

            {/* Tabela */}
            <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '11px', minWidth: '900px' }}>
                    <thead>
                        <tr>
                            <th rowSpan={2} style={{ ...sTh, width: '72px' }}>DATA</th>
                            <th rowSpan={2} style={{ ...sTh, minWidth: '110px' }}>LOCALIDADE</th>
                            <th rowSpan={2} style={{ ...sTh, width: '38px', writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap' }}>CONCLUÍDA</th>
                            <th colSpan={3} style={sTh}>CASAS TRABALHADAS</th>
                            <th colSpan={3} style={sTh}>CASAS PENDENTES</th>
                            <th colSpan={3} style={sTh}>ANEXO TRABALHADOS</th>
                            <th colSpan={3} style={sTh}>UNIDADES DOMICILIARES</th>
                            <th rowSpan={2} style={{ ...sTh, width: '52px' }}>HABITANTES DIRETAMENTE PROTEGIDOS</th>
                            <th rowSpan={2} style={{ ...sTh, width: '52px' }}>CONSUMO DE INSETICIDA (BHC cps)</th>
                            <th rowSpan={2} style={{ ...sTh, width: '44px' }}>Nº DE TRIAT.</th>
                            <th rowSpan={2} style={{ ...sTh, width: '30px' }}>HD</th>
                            <th rowSpan={2} style={{ ...sTh, width: '28px' }}></th>
                        </tr>
                        <tr>
                            {[
                                'POSITIVAS','NEGATIVAS','TOTAL',
                                'FECHADAS','RECUSAS','TOTAL',
                                'POSITIVOS','NEGATIVOS','TOTAL',
                                'POSITIVAS','NEGATIVAS','TOTAL'
                            ].map((h, i) => (
                                <th key={i} style={{ ...sTh, width: '40px' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {withTotals.map((l, idx) => (
                            <tr key={l._id}>
                                <td style={sTd}><input type="date" style={{ ...sIn, width: '100%' }} value={l.data} onChange={e => setLinha(idx, 'data', e.target.value)} /></td>
                                <td style={sTd}><input style={sIn} value={l.localidade} onChange={e => setLinha(idx, 'localidade', e.target.value)} /></td>
                                <td style={sTd}><input style={{ ...sNum, width: '100%' }} maxLength={1} value={l.concluida} onChange={e => setLinha(idx, 'concluida', e.target.value.toUpperCase())} /></td>
                                {[['ct_pos'],['ct_neg'],['ct_total'],['cp_fech'],['cp_rec'],['cp_total'],['at_pos'],['at_neg'],['at_total'],['ud_pos'],['ud_neg'],['ud_total']].map(([f]) => (
                                    <td key={f} style={sTd}>
                                        <input type="number" min="0" style={sNum}
                                            value={f.endsWith('total') ? (l[f] || '') : (l[f] || '')}
                                            readOnly={f.endsWith('total')}
                                            onChange={e => setLinha(idx, f, e.target.value)}
                                        />
                                    </td>
                                ))}
                                <td style={sTd}><input type="number" min="0" style={sNum} value={l.hab_proteg} onChange={e => setLinha(idx, 'hab_proteg', e.target.value)} /></td>
                                <td style={sTd}><input type="number" min="0" style={sNum} value={l.consumo_inset} onChange={e => setLinha(idx, 'consumo_inset', e.target.value)} /></td>
                                <td style={sTd}><input type="number" min="0" style={sNum} value={l.n_triat} onChange={e => setLinha(idx, 'n_triat', e.target.value)} /></td>
                                <td style={sTd}><input style={{ ...sNum, width: '100%' }} value={l.hd} onChange={e => setLinha(idx, 'hd', e.target.value)} /></td>
                                <td style={{ ...sTd, textAlign: 'center' }}>
                                    <button onClick={() => removeLinha(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53e3e', fontSize: '14px', lineHeight: 1 }} title="Remover linha">×</button>
                                </td>
                            </tr>
                        ))}
                        {/* Linha TOTAL */}
                        <tr>
                            <td colSpan={3} style={{ ...sTot, textAlign: 'center' }}>TOTAL</td>
                            {['ct_pos','ct_neg','ct_total','cp_fech','cp_rec','cp_total','at_pos','at_neg','at_total','ud_pos','ud_neg','ud_total','hab_proteg','consumo_inset','n_triat'].map(f => (
                                <td key={f} style={sTot}>{totalRow[f] || ''}</td>
                            ))}
                            <td style={sTot}></td>
                            <td style={sTot}></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <button onClick={addLinha} style={{ marginTop: '8px', fontSize: '12px', padding: '4px 12px', background: '#e2e8f0', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}>
                + Adicionar linha
            </button>

            {/* Rodapé */}
            <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', fontSize: '12px', borderTop: '1px solid #ccc', paddingTop: '14px' }}>
                <div>
                    <label style={{ fontSize: '10px', color: '#555', display: 'block', marginBottom: '2px' }}>Rendimento / Homem / Dia</label>
                    <input style={sIn} value={rodape.rendimento} onChange={e => setRod('rendimento', e.target.value)} />
                </div>
                <div>
                    <label style={{ fontSize: '10px', color: '#555', display: 'block', marginBottom: '2px' }}>Saldo de BHC ou K-O</label>
                    <input style={sIn} value={rodape.saldo_bhc} onChange={e => setRod('saldo_bhc', e.target.value)} />
                </div>
                <div>
                    <label style={{ fontSize: '10px', color: '#555', display: 'block', marginBottom: '2px' }}>Data</label>
                    <input type="date" style={sIn} value={rodape.data_ass} onChange={e => setRod('data_ass', e.target.value)} />
                </div>
                <div>
                    <label style={{ fontSize: '10px', color: '#555', display: 'block', marginBottom: '2px' }}>Ass. Guarda Chefe</label>
                    <input style={sIn} value={rodape.ass_guarda} onChange={e => setRod('ass_guarda', e.target.value)} />
                </div>
                <div>
                    <label style={{ fontSize: '10px', color: '#555', display: 'block', marginBottom: '2px' }}>Visto Inspetor</label>
                    <input style={sIn} value={rodape.ass_inspetor} onChange={e => setRod('ass_inspetor', e.target.value)} />
                </div>
            </div>

            {/* Botão Salvar */}
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
                <button
                    onClick={handleSalvar}
                    disabled={salvando}
                    style={{ padding: '9px 28px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '700', fontSize: '13px', cursor: salvando ? 'not-allowed' : 'pointer', opacity: salvando ? 0.7 : 1 }}
                >
                    {salvando ? 'Salvando...' : editandoId ? '💾 Salvar Alterações' : '💾 Salvar Resumo Semanal'}
                </button>
            </div>

            {/* ── Lista de resumos salvos ── */}
            <div style={{ marginTop: '40px', borderTop: '2px solid #e2e8f0', paddingTop: '20px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '10px' }}>Resumos Semanais Cadastrados</h3>
                {loadingResumos && <p style={{ fontSize: '13px', color: '#64748b' }}>Carregando...</p>}
                {!loadingResumos && resumos.length === 0 && <p style={{ fontSize: '13px', color: '#64748b' }}>Nenhum resumo cadastrado.</p>}
                {resumos.map(r => {
                    const isOpen = expandedId === r.id;
                    const linhasR = (r.resumos_borrifacao_linhas || []).sort((a, b) => a.ordem - b.ordem);
                    const dataFormatada = r.created_at ? new Date(r.created_at).toLocaleDateString('pt-BR') : '';
                    return (
                        <div key={r.id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '8px', overflow: 'hidden' }}>
                            {/* Cabeçalho clicável */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f8fafc', userSelect: 'none' }}>
                                {/* Lado esquerdo — clicável para expandir */}
                                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', flex: 1, cursor: 'pointer' }} onClick={() => setExpandedId(isOpen ? null : r.id)}>
                                    <span style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b' }}>
                                        Semana: {r.semana || '—'}
                                    </span>
                                    {r.guarda_chefe  && <span style={{ fontSize: '12px', color: '#475569' }}>Guarda Chefe: {r.guarda_chefe}</span>}
                                    {r.turma         && <span style={{ fontSize: '12px', color: '#475569' }}>Turma: {r.turma}</span>}
                                    {r.fase_trabalho && <span style={{ fontSize: '12px', color: '#475569' }}>Fase: {r.fase_trabalho}</span>}
                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>Salvo em: {dataFormatada}</span>
                                </div>
                                {/* Lado direito — botões + seta */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                    <button
                                        onClick={() => handleCarregarParaEdicao(r)}
                                        style={{ fontSize: '11px', padding: '3px 10px', background: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                                    >Visualizar / Alterar</button>
                                    <button
                                        onClick={() => handleExcluir(r.id)}
                                        style={{ fontSize: '11px', padding: '3px 10px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                                    >🗑️ Excluir</button>
                                    <span style={{ fontSize: '16px', color: '#64748b', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', cursor: 'pointer' }} onClick={() => setExpandedId(isOpen ? null : r.id)}>▼</span>
                                </div>
                            </div>

                            {/* Conteúdo expandido — formulário completo em modo leitura */}
                            {isOpen && (
                                <div style={{ padding: '16px' }}>
                                    {/* Título */}
                                    <div style={{ textAlign: 'center', fontWeight: '800', fontSize: '13px', letterSpacing: '0.5px', marginBottom: '10px' }}>
                                        RESUMO SEMANAL DE BORRIFAÇÃO
                                    </div>
                                    {/* Cabeçalho linha 1 */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr 1fr', gap: '6px', marginBottom: '6px', fontSize: '12px' }}>
                                        {[['DIST. SANIT.', r.dist_sanit], ['SUB-DISTRITO', r.sub_distrito], ['MUNICÍPIO', r.municipio_nome], ['FASE DE TRABALHO', r.fase_trabalho]].map(([lbl, val]) => (
                                            <div key={lbl} style={{ borderBottom: '1px solid #555', paddingBottom: '2px' }}>
                                                <div style={{ fontSize: '9px', color: '#666' }}>{lbl}</div>
                                                <div style={{ fontSize: '12px', fontWeight: '600', minHeight: '16px' }}>{val || ''}</div>
                                            </div>
                                        ))}
                                    </div>
                                    {/* Cabeçalho linha 2 */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '80px 80px 1fr', gap: '6px', marginBottom: '14px', fontSize: '12px' }}>
                                        {[['SEMANA', r.semana], ['TURMA', r.turma], ['NOME GUARDA CHEFE', r.guarda_chefe]].map(([lbl, val]) => (
                                            <div key={lbl} style={{ borderBottom: '1px solid #555', paddingBottom: '2px' }}>
                                                <div style={{ fontSize: '9px', color: '#666' }}>{lbl}</div>
                                                <div style={{ fontSize: '12px', fontWeight: '600', minHeight: '16px' }}>{val || ''}</div>
                                            </div>
                                        ))}
                                    </div>
                                    {/* Tabela completa */}
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '11px', minWidth: '900px' }}>
                                            <thead>
                                                <tr>
                                                    <th rowSpan={2} style={{ ...sTh, width: '72px' }}>DATA</th>
                                                    <th rowSpan={2} style={{ ...sTh, minWidth: '110px' }}>LOCALIDADE</th>
                                                    <th rowSpan={2} style={{ ...sTh, width: '38px', writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap' }}>CONCLUÍDA</th>
                                                    <th colSpan={3} style={sTh}>CASAS TRABALHADAS</th>
                                                    <th colSpan={3} style={sTh}>CASAS PENDENTES</th>
                                                    <th colSpan={3} style={sTh}>ANEXO TRABALHADOS</th>
                                                    <th colSpan={3} style={sTh}>UNIDADES DOMICILIARES</th>
                                                    <th rowSpan={2} style={{ ...sTh, width: '52px' }}>HABITANTES DIRETAMENTE PROTEGIDOS</th>
                                                    <th rowSpan={2} style={{ ...sTh, width: '52px' }}>CONSUMO DE INSETICIDA (BHC cps)</th>
                                                    <th rowSpan={2} style={{ ...sTh, width: '44px' }}>Nº DE TRIAT.</th>
                                                    <th rowSpan={2} style={{ ...sTh, width: '30px' }}>HD</th>
                                                </tr>
                                                <tr>
                                                    {['POSITIVAS','NEGATIVAS','TOTAL','FECHADAS','RECUSAS','TOTAL','POSITIVOS','NEGATIVOS','TOTAL','POSITIVAS','NEGATIVAS','TOTAL'].map((h, i) => (
                                                        <th key={i} style={{ ...sTh, width: '40px' }}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {linhasR.map((l, i) => (
                                                    <tr key={l.id || i}>
                                                        <td style={{ ...sTd, textAlign: 'center' }}>{l.data ? new Date(`${l.data}T00:00:00`).toLocaleDateString('pt-BR') : ''}</td>
                                                        <td style={sTd}>{l.localidade}</td>
                                                        <td style={{ ...sTd, textAlign: 'center' }}>{l.concluida}</td>
                                                        {['ct_pos','ct_neg','ct_total','cp_fech','cp_rec','cp_total','at_pos','at_neg','at_total','ud_pos','ud_neg','ud_total','hab_proteg','consumo_inset','n_triat','hd'].map(f => (
                                                            <td key={f} style={{ ...sTd, textAlign: 'center' }}>{l[f] ?? ''}</td>
                                                        ))}
                                                    </tr>
                                                ))}
                                                {/* Linha TOTAL */}
                                                <tr>
                                                    <td colSpan={3} style={{ ...sTot, textAlign: 'center' }}>TOTAL</td>
                                                    {['ct_pos','ct_neg','ct_total','cp_fech','cp_rec','cp_total','at_pos','at_neg','at_total','ud_pos','ud_neg','ud_total','hab_proteg','consumo_inset','n_triat'].map(f => (
                                                        <td key={f} style={sTot}>
                                                            {linhasR.reduce((s, l) => s + (Number(l[f]) || 0), 0) || ''}
                                                        </td>
                                                    ))}
                                                    <td style={sTot}></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    {/* Rodapé */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '14px', fontSize: '12px', borderTop: '1px solid #ccc', paddingTop: '12px' }}>
                                        {[['Rendimento / Homem / Dia', r.rendimento], ['Saldo de BHC ou K-O', r.saldo_bhc], ['Data', r.data_ass ? new Date(`${r.data_ass}T00:00:00`).toLocaleDateString('pt-BR') : ''], ['Ass. Guarda Chefe', r.ass_guarda], ['Visto Inspetor', r.ass_inspetor]].map(([lbl, val]) => (
                                            <div key={lbl} style={{ borderBottom: '1px solid #ccc', paddingBottom: '4px' }}>
                                                <div style={{ fontSize: '9px', color: '#666' }}>{lbl}</div>
                                                <div style={{ fontSize: '12px', fontWeight: '600', minHeight: '16px' }}>{val || ''}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────
   COMPONENTE PRINCIPAL — Controle Vetorial
───────────────────────────────────────────── */
const VigilanciaPassivaTab = ({ casos = [], kpis = {}, municipioId, onAtenderDenuncia, onRecusarDenuncia }) => {
    const [modalCaso, setModalCaso] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [expandedCasoId, setExpandedCasoId] = useState(null);
    const navigate = useNavigate();

    const [filtros, setFiltros] = useState({
        origem: 'todos',
        busca: '',
    });

    const [modalState, setModalState] = useState({ isOpen: false, images: [], currentIndex: 0 });
    const [visibleCount, setVisibleCount] = useState(20);
    const observer = useRef();

    const handleFiltroChange = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
    };

    const handleAtenderClick = useCallback((caso) => {
        if (onAtenderDenuncia) {
            onAtenderDenuncia(caso);
        } else {
            navigate('/ficha-campo', { state: { data: caso } });
        }
    }, [navigate, onAtenderDenuncia]);

    const openModal = (images, index) => {
        if (!images || images.length === 0) return;
        setModalState({ isOpen: true, images: images, currentIndex: index });
    };
    const closeModal = () => {
        setModalState({ isOpen: false, images: [], currentIndex: 0 });
    };
    const nextImage = (e) => {
        e.stopPropagation();
        setModalState(prev => ({ ...prev, currentIndex: (prev.currentIndex + 1) % prev.images.length }));
    };
    const prevImage = (e) => {
        e.stopPropagation();
        setModalState(prev => ({ ...prev, currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length }));
    };

    const getSortableDate = (c) => {
        if (c.denuncia_data) return new Date(c.denuncia_data);
        if (c.data_atividade) return new Date(`${c.data_atividade}T00:00:00.000Z`);
        return null;
    };

    const sortedCasos = useMemo(() => {
        if (!casos || casos.length === 0) return [];
        return [...casos].sort((a, b) => {
            const dateA = getSortableDate(a);
            const dateB = getSortableDate(b);
            if (!dateA) return 1;
            if (!dateB) return -1;
            return dateB - dateA;
        });
    }, [casos]);

    const filteredCasos = useMemo(() => {
        setVisibleCount(20);
        return sortedCasos.filter(caso => {
            if (!caso.denuncia_id) return false;
            if (filtros.origem !== 'todos' && caso.origem_status !== filtros.origem) return false;
            if (filtros.busca) {
                const buscaLower = filtros.busca.toLowerCase();
                const textoDoCard = [caso.denuncia_rua, caso.denuncia_bairro, caso.denuncia_descricao, caso.agente_nome, caso.nome_morador].filter(Boolean).join(' ').toLowerCase();
                if (!textoDoCard.includes(buscaLower)) return false;
            }
            return true;
        });
    }, [sortedCasos, filtros]);

    const lastCardElementRef = useCallback(node => {
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && visibleCount < filteredCasos.length) {
                setVisibleCount(prevVisibleCount => prevVisibleCount + 20);
            }
        });
        if (node) observer.current.observe(node);
    }, [visibleCount, filteredCasos.length]);


    useEffect(() => {
        if (!casos || casos.length === 0) return;
        const handleKeyDown = (e) => {
            if (!modalState.isOpen) return;
            if (e.key === 'ArrowRight') nextImage(e);
            else if (e.key === 'ArrowLeft') prevImage(e);
            else if (e.key === 'Escape') closeModal();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [modalState.isOpen, casos]);


    return (
        <div className="vigilancia-ativa-container">

            {/* Modal de imagem — disponível em ambas as sub-abas */}
            {modalState.isOpen && (
                <div className="image-modal-backdrop" onClick={closeModal}>
                    {modalState.images.length > 1 && <button className="image-modal-nav prev" onClick={prevImage}>&#10094;</button>}
                    <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                        <img src={modalState.images[modalState.currentIndex]} alt={`Visualização ampliada ${modalState.currentIndex + 1} de ${modalState.images.length}`} />
                    </div>
                    {modalState.images.length > 1 && <button className="image-modal-nav next" onClick={nextImage}>&#10095;</button>}
                </div>
            )}

            {/* ── Conteúdo: Registros ── */}
            <>

            {/* Tabs de status + busca */}
            <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    {[
                        { value: 'todos',             label: 'Todas' },
                        { value: 'Denúncia Pendente', label: 'Pendentes' },
                        { value: 'Denúncia Atendida', label: 'Atendidas' },
                        { value: 'Denúncia Recusada', label: 'Recusadas' },
                    ].map(({ value, label }) => (
                        <button
                            key={value}
                            onClick={() => setFiltros(prev => ({ ...prev, origem: value }))}
                            style={{
                                padding: '6px 18px', fontSize: '13px', fontWeight: filtros.origem === value ? '600' : '500',
                                border: `1.5px solid ${filtros.origem === value ? '#111827' : '#e5e7eb'}`,
                                borderRadius: '20px',
                                background: filtros.origem === value ? '#111827' : '#ffffff',
                                color: filtros.origem === value ? '#ffffff' : '#6b7280',
                                cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit',
                            }}
                        >{label}</button>
                    ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '7px 12px' }}>
                    <SearchIcon />
                    <input
                        type="text"
                        placeholder="Buscar por endereço, descrição ou agente..."
                        name="busca"
                        value={filtros.busca}
                        onChange={handleFiltroChange}
                        style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '13px', width: '100%', fontFamily: 'inherit', color: '#374151' }}
                    />
                </div>
            </div>

            {/* Modal de detalhes */}
            {modalCaso && (
                <DenunciaDetailModal
                    caso={modalCaso}
                    onClose={() => setModalCaso(null)}
                    onAtender={handleAtenderClick}
                    onRecusar={onRecusarDenuncia}
                    onImageClick={openModal}
                />
            )}

            {/* Lista de denúncias */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {filteredCasos && filteredCasos.length > 0 ? (
                    filteredCasos.slice(0, visibleCount).map((caso, index) => {
                        const cardContent = (
                            <CasoCard
                                key={caso.denuncia_id || caso.caso_id}
                                caso={caso}
                                onCardClick={(c) => setModalCaso(c)}
                                onImageClick={openModal}
                            />
                        );
                        if (filteredCasos.slice(0, visibleCount).length === index + 1) {
                            return <div ref={lastCardElementRef} key={caso.denuncia_id || caso.caso_id}>{cardContent}</div>;
                        }
                        return cardContent;
                    })
                ) : (
                    <div className="empty-state"><p>Nenhuma denúncia encontrada.</p></div>
                )}
            </div>

            {visibleCount < filteredCasos.length && (
                <div className="loading-indicator"><p>Carregando mais...</p></div>
            )}
            </>

        </div>
    );
};

export default VigilanciaPassivaTab;
